import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useRef, useState } from 'react';

type UseStateRef = {
  <T>(initialState: T): [T, Dispatch<SetStateAction<T>>, MutableRefObject<T>];
  <T = undefined>(): [T | undefined, Dispatch<SetStateAction<T | undefined>>, MutableRefObject<T | undefined>];
};

/**
 * A hook that uses both a state and ref variable and keeps both in sync. This is useful when state variables are
 * causing closure issues in certain functions.
 * @param {(() => T) | T} initialValue - The initial value to set the state and ref to.
 * @returns {[(T | undefined), (<T>(value: ((<T>(prevState: (T | undefined)) => (T | undefined)) | T | undefined)) => void), React.MutableRefObject<(() => T) | T | undefined>]}
 */
const useStateRef: UseStateRef = <T>(initialValue?: T) => {
  const [state, setState] = useState(initialValue);
  const ref = useRef(initialValue);

  if (ref.current !== state) ref.current = state;

  return [state, setState, ref] as [typeof state, typeof setState, typeof ref];
};

export default useStateRef;
